import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Ticket, Copy, Ban } from "lucide-react";

const TYPES = ["influencer", "hairdresser", "salon", "partner", "tester"];

export default function AdminComplimentary() {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("influencer");
  const [nameField, setNameField] = useState("");
  const [campaign, setCampaign] = useState("");
  const [notes, setNotes] = useState("");
  const [uses, setUses] = useState(1);
  const [days, setDays] = useState(30);
  const [credit, setCredit] = useState(false);

  const load = () => axios.get(`${API}/admin/complimentary`).then((r) => setRows(r.data.items || []));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await axios.post(`${API}/admin/complimentary`, {
        recipient_type: type,
        recipient_name: nameField,
        campaign,
        notes,
        max_uses: Number(uses),
        expires_in_days: Number(days),
        include_purchase_credit: credit,
      });
      toast.success("Code generated");
      setNameField(""); setCampaign(""); setNotes(""); setUses(1); setDays(30); setCredit(false);
      load();
    } catch (e) {
      toast.error("Could not generate code");
    }
  };

  const deactivate = async (code) => {
    await axios.delete(`${API}/admin/complimentary/${code}`);
    load();
  };
  const copy = async (code) => {
    await navigator.clipboard.writeText(code);
    toast.success("Copied");
  };

  return (
    <div data-testid="admin-complimentary">
      <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">Complimentary Access</div>
      <h1 className="font-serif text-3xl text-[#383633] tracking-tight mb-8">Influencers, hairdressers, salons, partners, testers</h1>

      <div className="bg-white border kt-line rounded-2xl p-6 kt-shadow mb-8">
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-4 flex items-center gap-2">
          <Ticket className="h-3.5 w-3.5" /> Generate new code
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Recipient type</Label>
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="comp-type"
              className="mt-2 w-full h-11 rounded-md border border-[#ECE7DF] bg-white px-3 text-sm text-[#383633]">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Recipient name / handle</Label>
            <Input value={nameField} onChange={(e) => setNameField(e.target.value)} className="mt-2 h-11 border-[#ECE7DF]" data-testid="comp-name" />
          </div>
          <div>
            <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Campaign / salon</Label>
            <Input value={campaign} onChange={(e) => setCampaign(e.target.value)} className="mt-2 h-11 border-[#ECE7DF]" data-testid="comp-campaign" />
          </div>
          <div>
            <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Max uses</Label>
            <Input type="number" min="1" max="1000" value={uses} onChange={(e) => setUses(e.target.value)} className="mt-2 h-11 border-[#ECE7DF]" data-testid="comp-uses" />
          </div>
          <div>
            <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Expires in (days)</Label>
            <Input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} className="mt-2 h-11 border-[#ECE7DF]" data-testid="comp-days" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Internal notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2 h-11 border-[#ECE7DF]" data-testid="comp-notes" />
          </div>
          <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer" data-testid="comp-credit-row">
            <Checkbox checked={credit} onCheckedChange={(v) => setCredit(!!v)}
              className="border-[#6F6B66] mt-0.5 data-[state=checked]:bg-[#383633] data-[state=checked]:border-[#383633]" data-testid="comp-credit" />
            <span className="text-xs text-[#6F6B66] leading-relaxed">
              Also include €9.88 Shopify purchase credit <span className="text-[#A8AD94]">(off by default)</span>
            </span>
          </label>
        </div>
        <Button onClick={create} data-testid="comp-generate"
          className="mt-6 rounded-full h-11 bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase">
          Generate code
        </Button>
      </div>

      <div className="bg-white border kt-line rounded-2xl overflow-hidden" data-testid="comp-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F6F2] text-[#6F6B66]">
            <tr>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Code</th>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Type</th>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Recipient</th>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Uses</th>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Credit</th>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Expires</th>
              <th className="text-left px-4 py-3 kt-tracking uppercase text-[10px]">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code} className="border-t kt-line">
                <td className="px-4 py-3 font-mono text-[#383633]">{r.code}</td>
                <td className="px-4 py-3 text-xs kt-tracking uppercase text-[#6F6B66]">{r.recipient_type}</td>
                <td className="px-4 py-3 text-[#383633]">{r.recipient_name || <span className="text-[#6F6B66]">—</span>}</td>
                <td className="px-4 py-3 font-mono">{r.uses}/{r.max_uses}</td>
                <td className="px-4 py-3 text-xs">{r.include_purchase_credit ? "€9.88" : "—"}</td>
                <td className="px-4 py-3 text-[#6F6B66] font-mono text-xs">{r.expires_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-xs kt-tracking uppercase">
                  {r.active ? <span className="text-[#A8AD94]">Active</span> : <span className="text-[#6F6B66]">Paused</span>}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button onClick={() => copy(r.code)} className="inline-flex items-center gap-1 text-xs text-[#6F6B66] hover:text-[#383633] mr-3">
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </button>
                  {r.active && (
                    <button onClick={() => deactivate(r.code)} className="inline-flex items-center gap-1 text-xs text-[#6F6B66] hover:text-[#A85C50]">
                      <Ban className="h-3.5 w-3.5" /> Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#6F6B66]">No codes yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
